import {
  ActionIcon,
  Flex,
  Indicator,
  Popover,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFileSpreadsheet, IconFilter } from "@tabler/icons-react";
import { inventoryApi } from "apis/inventoryApi";
import CreatableSelect from "components/ui/CreatableSelect/CreatableSelect";
import GroupButton, {
  ActionButton,
} from "components/ui/GroupButton/GroupButton";
import { SearchInput } from "components/ui/SearchInput/SearchInput";
import { CommonParamsType } from "constants/types/common";
import { InventoryParamsType } from "constants/types/inventory";
import { FC, useMemo, useState } from "react";
import { usePopoverClickOutside } from "utils/hooks/ui/usePopoverClickOutside";
import { useNotification } from "utils/hooks";
import { useListProduct } from "utils/hooks/request/useProducts";
import { useListWarehouse } from "utils/hooks/request/useWarehouses";

type FilterFields = Omit<InventoryParamsType, keyof CommonParamsType>;

type InventoryActionsProps = {
  handleSearch: (v: string) => void;
  params: InventoryParamsType;
  onFilterChange: (filters: Partial<FilterFields>) => void;
};

const InventoryActions: FC<InventoryActionsProps> = ({
  handleSearch,
  params,
  onFilterChange,
}) => {
  const [filterOpened, { close: closeFilter, toggle: toggleFilter }] =
    useDisclosure(false);
  const { triggerRef, dropdownRef } = usePopoverClickOutside(filterOpened, closeFilter);
  const [isExporting, setIsExporting] = useState(false);
  const { notification } = useNotification();

  const { warehouseOptions, isLoading: warehousesLoading } = useListWarehouse();
  const { productOptions, isLoading: productsLoading } = useListProduct();

  const activeFilterCount = useMemo(
    () => [params.warehouseId, params.productId].filter(Boolean).length,
    [params.warehouseId, params.productId],
  );

  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const exportParams = {
        ...(params.search ? { search: params.search } : {}),
        ...(params.warehouseId ? { warehouseId: params.warehouseId } : {}),
        ...(params.productId ? { productId: params.productId } : {}),
      };
      const res = await inventoryApi.export(exportParams);
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "ton-kho.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      notification.success({ title: "Xuất Excel thành công" });
    } catch {
      notification.error({ title: "Xuất Excel thất bại" });
    } finally {
      setIsExporting(false);
    }
  };

  const actions: ActionButton[] = [
    {
      key: "export-excel",
      label: "Xuất Excel",
      icon: <IconFileSpreadsheet size={18} />,
      variant: "filled",
      color: "#217346",
      isLoading: isExporting,
      disabled: isExporting,
      onClick: handleExportExcel,
      permission: "inventory:export",
    },
  ];

  return (
    <Flex justify="space-between" align="center">
      <Flex align="center" gap="sm">
        <SearchInput
          value={params.search || ""}
          onChange={handleSearch}
          placeholder="Tìm kiếm tồn kho"
        />
        <Popover
          opened={filterOpened}
          onClose={closeFilter}
          position="bottom-start"
          shadow="md"
          withinPortal
          closeOnClickOutside={false}
        >
          <Popover.Target>
            <Indicator
              label={activeFilterCount}
              size={16}
              disabled={activeFilterCount === 0}
              color="blue"
            >
              <ActionIcon
                ref={triggerRef}
                variant="default"
                size="lg"
                onClick={toggleFilter}
                aria-label="Bộ lọc"
              >
                <IconFilter size={18} />
              </ActionIcon>
            </Indicator>
          </Popover.Target>

          <Popover.Dropdown>
            <div ref={dropdownRef}>
            <Stack gap="sm" w={280}>
              <Text size="sm" fw={600}>
                Bộ lọc
              </Text>
              <CreatableSelect
                label="Kho"
                placeholder="Tất cả"
                data={warehouseOptions ?? []}
                value={params.warehouseId ?? null}
                onChange={(v) =>
                  onFilterChange({ warehouseId: v ?? undefined })
                }
                allowCreate={false}
                clearable
                disabled={warehousesLoading}
                size="sm"
              />
              <CreatableSelect
                label="Mặt hàng"
                placeholder="Tất cả"
                data={productOptions ?? []}
                value={params.productId ?? null}
                onChange={(v) => onFilterChange({ productId: v ?? undefined })}
                allowCreate={false}
                clearable
                disabled={productsLoading}
                size="sm"
              />
            </Stack>
            </div>
          </Popover.Dropdown>
        </Popover>
      </Flex>
      <GroupButton actions={actions} />
    </Flex>
  );
};

export default InventoryActions;
